import React, { useEffect } from 'react';
import { Modal, Form, Input, Switch, message } from 'antd';
import { useSaveDocumentTypeMutation } from '../../services/settings.service';

const DocumentTypeModal = ({ visible, documentType, onClose, onSuccess }) => {
  const [form] = Form.useForm();
  const [saveDocumentType, { isLoading }] = useSaveDocumentTypeMutation();

  useEffect(() => {
    if (visible) {
      if (documentType) {
        // Editing existing document type
        form.setFieldsValue({
          name: documentType.name,
          description: documentType.description || '',
          isRequired: documentType.isRequired || false,
          hasExpiry: documentType.hasExpiry || false,
          isActive: documentType.isActive !== undefined ? documentType.isActive : true,
        });
      } else {
        // Adding new document type - set defaults
        form.setFieldsValue({
          name: '',
          description: '',
          isRequired: false,
          hasExpiry: false,
          isActive: true,
        });
      }
    }
  }, [visible, documentType, form]);

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();

      const payload = {
        ...values,
        ...(documentType?.id && { id: documentType.id }),
      };

      await saveDocumentType(payload).unwrap();

      message.success(
        `Document type ${documentType ? 'updated' : 'created'} successfully`
      );

      form.resetFields();
      onSuccess();
    } catch (error) {
      if (error?.data?.message) {
        message.error(error.data.message);
      } else if (error?.errorFields) {
        message.error('Please fill in all required fields');
      } else {
        message.error('Failed to save document type');
      }
    }
  };

  const handleCancel = () => {
    form.resetFields();
    onClose();
  };

  return (
    <Modal
      title={documentType ? 'Edit Document Type' : 'Add Document Type'}
      open={visible}
      onOk={handleSubmit}
      onCancel={handleCancel}
      confirmLoading={isLoading}
      width={600}
      okText={documentType ? 'Update' : 'Create'}
    >
      <Form
        form={form}
        layout="vertical"
        autoComplete="off"
      >
        <Form.Item
          name="name"
          label="Document Type Name"
          rules={[
            { required: true, message: 'Please enter document type name' },
            { min: 3, message: 'Name must be at least 3 characters' },
            { max: 100, message: 'Name cannot exceed 100 characters' },
          ]}
        >
          <Input
            placeholder="e.g., Passport, National ID, Driver's License"
            size="large"
          />
        </Form.Item>

        <Form.Item
          name="description"
          label="Description"
          rules={[
            { max: 500, message: 'Description cannot exceed 500 characters' },
          ]}
        >
          <Input.TextArea
            placeholder="Brief description of the document type (optional)"
            rows={3}
            size="large"
          />
        </Form.Item>

        <Form.Item
          name="isRequired"
          label="Required Document"
          valuePropName="checked"
          tooltip="Mark this document as mandatory for all employees"
        >
          <Switch
            checkedChildren="Required"
            unCheckedChildren="Optional"
          />
        </Form.Item>

        <Form.Item
          name="hasExpiry"
          label="Has Expiry Date"
          valuePropName="checked"
          tooltip="Does this document type have an expiration date?"
        >
          <Switch
            checkedChildren="Yes"
            unCheckedChildren="No"
          />
        </Form.Item>

        <Form.Item
          name="isActive"
          label="Active Status"
          valuePropName="checked"
        >
          <Switch
            checkedChildren="Active"
            unCheckedChildren="Inactive"
          />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default DocumentTypeModal;
